import {
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IssuedInternalApiKey,
  internalApiKeysApi,
} from '@texas/api/endpoints/internalApiKeysApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';

interface IssueKeyForm {
  name: string;
}

export function IssueInternalApiKeyModal({
  isOpen,
  onClose,
  onIssued,
}: {
  isOpen: boolean;
  onClose: () => void;
  onIssued: (apiKey: IssuedInternalApiKey) => void;
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<IssueKeyForm>({ defaultValues: { name: '' } });
  const {
    request: issue,
    loading,
    error,
  } = useApiRequest(internalApiKeysApi.issue);

  useEffect(() => {
    if (isOpen) reset({ name: '' });
  }, [isOpen, reset]);

  const onSubmit = async ({ name }: IssueKeyForm) => {
    const response = await issue(name);
    if (!response.hasError) onIssued(response.data);
  };

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('internalApiKeys.generate')}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', {
                    required: t('errors.nameRequired', {
                      name: t('general.name'),
                    }),
                    maxLength: {
                      value: 100,
                      message: t('errors.nameMaxLength', {
                        name: t('general.name'),
                        count: 100,
                      }),
                    },
                  })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <ErrorDetails error={error} />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={loading}>
              {t('internalApiKeys.generate')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
